// pms-core / domain — THE DESK'S OFFER (PB3, PMS-PHONE-BOOKING-BRIEF §5).
//
// Pure. The facts are `pms_desk_offer`'s: the website's (`pms_public_offer`,
// one derivation) plus each category's standing and whether the établissement
// may lodge a full category bigger. Nothing is priced here that `quoteStay`
// does not price, and nothing is lodged that `sellAs` does not lodge — so the
// desk's quote equals the site's for the same stay and party (P1), and the
// site's door (PB6) will lodge exactly as the desk does (P10).
//
// One option per category × root plan, in the facts' order: sellable — sold,
// hosted, priced — or the reason it is not. The desk shows them all; which to
// propose first is the screen's (PB5), not a rule here.

import { quoteStay, sellAs, stayRestrictionsBroken, withoutStayRestrictions, type DirectQuote, type StayRestriction, type OfferCategory, type PublicOfferFacts, type RoomsLeft, type StayParty, type UnsellableReason } from './directQuote.js';
import type { RoomFit } from './roomFit.js';

export interface DeskOfferCategory extends OfferCategory {
  /** Every room of the category is superior — as `pms_category_hosts` measures it. */
  superior: boolean;
}

export interface DeskOfferFacts extends PublicOfferFacts {
  /** A full category may be lodged in a bigger one (P6) — a hotel only (Q7). */
  lodgeBigger: boolean;
  categories: DeskOfferCategory[];
}

/** The fit `sellAs` reads, from the facts alone. Beds are not asked when
 *  selling a category, so no layout is known here. */
export function deskRoomFit(facts: DeskOfferFacts): RoomFit {
  return {
    categories: new Map(facts.categories.map((c) => [c.id, { superior: c.superior, size: c.maxOccupancy }])),
    layoutOf: () => null,
  };
}

export type DeskRefusal = UnsellableReason | 'unknown_category' | 'unknown_plan';

export type DeskSale =
  | {
      sellable: true;
      category: string;
      plan: string;
      /** The category the stay will occupy: `category` itself unless it is full (P6). */
      host: string;
      lodged: boolean;
      /** The host is superior and the sold category is not — « surclassement ». */
      upgrade: boolean;
      /** Left in the HOST, as the site may say it. */
      left: RoomsLeft;
      /** Left in the HOST, exactly — the fewest free over the stay's nights.
       *  The desk sees the number (operator, 2026-10-27); the site says `left`. */
      available: number;
      /** Priced on the SOLD category, whatever the host. */
      quote: DirectQuote;
      /** CC2 — closed to arrival / departure or max stay this stay breaks.
       *  The site refuses such a stay; the desk is WARNED and may sell
       *  (operator, Q3). Empty when none. */
      restrictions: StayRestriction[];
      /** The arrival night's max stay, when `restrictions` names it. */
      maxStay?: number;
    }
  | { sellable: false; category: string; plan: string; reason: DeskRefusal; minStay?: number };

/** One category × plan for the party: where it would be lodged and its price. */
export function deskSale(
  facts: DeskOfferFacts,
  categoryCode: string,
  planCode: string,
  party: StayParty,
  fit: RoomFit = deskRoomFit(facts),
): DeskSale {
  const no = (reason: DeskRefusal, minStay?: number): DeskSale =>
    minStay === undefined
      ? { sellable: false, category: categoryCode, plan: planCode, reason }
      : { sellable: false, category: categoryCode, plan: planCode, reason, minStay };

  // Judged with CC2's three restrictions LIFTED: they warn the desk, they do
  // not refuse it (Q3). Everything else refuses exactly as on the site.
  const judged = withoutStayRestrictions(facts);
  const sale = sellAs(judged, fit, categoryCode, planCode, party, { lodgeBigger: facts.lodgeBigger });
  if (!sale.ok) return no(sale.reason, sale.minStay);
  const priced = quoteStay(judged, categoryCode, planCode, party, { lodged: sale.lodged });
  if (!priced.ok) return no(priced.reason, priced.minStay);
  const host = facts.categories.find((c) => c.code === sale.host);
  const available = host && host.nights.length > 0 ? Math.min(...host.nights.map((n) => n.available)) : 0;
  return {
    sellable: true,
    category: sale.sold,
    plan: planCode,
    host: sale.host,
    lodged: sale.lodged,
    upgrade: sale.upgrade,
    left: sale.left,
    available,
    quote: priced.quote,
    ...warnings(facts, categoryCode, planCode),
  };
}

function warnings(
  facts: DeskOfferFacts,
  categoryCode: string,
  planCode: string,
): { restrictions: StayRestriction[]; maxStay?: number } {
  const cat = facts.categories.find((c) => c.code === categoryCode);
  if (cat === undefined) return { restrictions: [] };
  const { broken, maxStay } = stayRestrictionsBroken(cat, planCode);
  return broken.includes('max_stay') && maxStay !== null ? { restrictions: broken, maxStay } : { restrictions: broken };
}

/** Every category × root plan for the party (P5: « every option for that party, with the three plans »). */
export function deskOptions(facts: DeskOfferFacts, party: StayParty): DeskSale[] {
  const fit = deskRoomFit(facts);
  return facts.categories.flatMap((c) => facts.plans.map((p) => deskSale(facts, c.code, p.code, party, fit)));
}
